import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { Decimal } from "@/utils/decimal";
import {
  formatLocaleBruttoPoints,
  formatLocaleCurrency,
  formatLocaleDate,
  formatLocaleNettoPoints,
} from "@/utils/formatUtils";
import { useUpdateFilterSearchParams } from "@/utils/hooksUtils";
import { getOrderDirection } from "@/utils/tableUtils";
import { isDefined, numberOrZero } from "@/utils/utils";
import { Card, Divider, Popover, Table } from "antd";
import { ColumnsType, ColumnType } from "antd/lib/table";
import dayjs from "dayjs";
import { t } from "i18next";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useDownloadPointsReportExport, useFilterPointsReport } from "../../../../common/api/queries";
import { TableSizes } from "../../../../common/constants";
import { ExportFileType, OrderDirection } from "../../../../common/enums";
import { SuperiorAgentsTable } from "../../../agent/components/tables/SuperiorAgentsTable";
import { AgentTypeTagWithName } from "../../../agent/components/tags/AgentTypeTagWithName";
import { formatAgentIdNumber } from "../../../agent/utils";
import { useIsCurrentAgentTypePlus } from "../../../auth/hooks";
import { DEFAULT_POINTS_QUERY_PARAMS } from "../api";
import { PointsReportOrderByColumn, PointsReportType } from "../enums";
import { NettoAndBruttoPointsBalance, PointsReportFilterPageRequest, PointsReportFilterPageResult } from "../types";
import {
  getPointsReportOrderByColumn,
  getPointsReportSortOrder,
  PointsReportColumnMeasure,
  PointsReportColumnScope,
} from "../utils";

const getDataForPeriodTogether = (item: NettoAndBruttoPointsBalance, index: number, reportType?: PointsReportType) => {
  const nettoPointsTogether = Decimal.sum(
    numberOrZero(item.individualNettoPointAmountsByPeriod?.[index]),
    numberOrZero(item.groupNettoPointAmountsByPeriod?.[index]),
  );
  const bruttoPointsTogether = Decimal.sum(
    numberOrZero(item.individualBruttoPointAmountsByPeriod?.[index]),
    numberOrZero(item.groupBruttoPointAmountsByPeriod?.[index]),
  );

  return {
    nettoPoints: reportType !== PointsReportType.BRUTTO_POINTS ? nettoPointsTogether : undefined,
    bruttoPoints: reportType !== PointsReportType.NETTO_POINTS ? bruttoPointsTogether : undefined,
  };
};

const getTotalDataTogether = (item: NettoAndBruttoPointsBalance, reportType?: PointsReportType) => {
  const nettoPointsTogether = Decimal.sum(
    numberOrZero(item.totalIndividualNettoPointAmount),
    numberOrZero(item.totalGroupNettoPointAmount),
  );
  const bruttoPointsTogether = Decimal.sum(
    numberOrZero(item.totalIndividualBruttoPointAmount),
    numberOrZero(item.totalGroupBruttoPointAmount),
  );

  return {
    nettoPoints: reportType !== PointsReportType.BRUTTO_POINTS ? nettoPointsTogether : undefined,
    bruttoPoints: reportType !== PointsReportType.NETTO_POINTS ? bruttoPointsTogether : undefined,
  };
};

interface PointsPeriod {
  label: string;
  startDate: string;
  endDate: string;
}

/** Kept on the column definition so that the table sorter change can read it back from there. */
type PointsReportColumnSorting = Pick<PointsReportFilterPageRequest, "orderBy" | "orderByPeriodIndex">;

const sortableColumnProps = (
  scope: PointsReportColumnScope,
  measure: PointsReportColumnMeasure,
  reportData?: PointsReportFilterPageResult,
  periodIndex?: number,
): ColumnType<NettoAndBruttoPointsBalance> & PointsReportColumnSorting => {
  const orderByColumn = getPointsReportOrderByColumn(scope, measure, reportData?.reportType);

  return {
    key: isDefined(periodIndex) ? `${orderByColumn}_${periodIndex}` : orderByColumn,
    sorter: true,
    sortOrder: getPointsReportSortOrder(orderByColumn, periodIndex, reportData),
    orderBy: [orderByColumn],
    orderByPeriodIndex: periodIndex,
  };
};

const commissionAmountColumn = (
  title: ReactNode,
  scope: PointsReportColumnScope,
  reportData?: PointsReportFilterPageResult,
  periodIndex?: number,
): ColumnType<NettoAndBruttoPointsBalance> & PointsReportColumnSorting => ({
  ...sortableColumnProps(scope, "SUM", reportData, periodIndex),
  title,
  align: "right",
  width: 260,
  render: (_, record) =>
    formatLocaleCurrency(
      (isDefined(periodIndex) ? record.commissionAmountsByPeriod?.[periodIndex] : record.totalCommissionAmount) ?? 0,
    ),
});

const getTableData = (
  isTopAgentPlus: boolean,
  reportData?: PointsReportFilterPageResult,
): { columns: ColumnsType<NettoAndBruttoPointsBalance>; periods: PointsPeriod[] } => {
  const { periodStartDate, periodEndDate, reportType } = reportData ?? {};

  const isCommissionAmountsReport = reportType === PointsReportType.COMMISSION_AMOUNTS;

  const columns: ColumnsType<NettoAndBruttoPointsBalance> = [
    {
      key: "agentName",
      title: t("common.agent"),
      fixed: "left",
      width: 240,
      ellipsis: { showTitle: false },
      render: (_, record) => (
        <AgentTypeTagWithName agent={record.agent} hideIdNumber ellipsis showLink showRepresentatives />
      ),
    },
    {
      key: "agentIdNumber",
      title: t("agent.attrs.idNumber"),
      fixed: "left",
      width: 100,
      ellipsis: { showTitle: false },
      render: (_, record) => formatAgentIdNumber(record.agent),
    },
    {
      key: "commissionsSettingsLevelCode",
      title: t("agent.helpers.commissionsSettingsLevel"),
      width: 100,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.agent.commissionsSettings.level?.code}</Ellipsis>,
    },
    {
      key: "commissionsSettingsRank",
      title: t("agent.helpers.commissionsSettingsRank"),
      width: 140,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.agent.commissionsSettings.rank?.name}</Ellipsis>,
    },
    {
      key: "superiorAgents",
      width: 50,
      align: "center",
      render: (_, record) => (
        <Popover
          title={t("agent.titles.superiorAgents")}
          styles={{ root: { maxWidth: 580 } }}
          content={<SuperiorAgentsTable agent={record.agent} />}
        >
          <span>
            <AntIcon color="blue" type="cluster" />
          </span>
        </Popover>
      ),
    },
  ];

  const periods: PointsPeriod[] = [];

  if (periodStartDate) {
    const maxPeriodEndDate = periodEndDate !== undefined ? dayjs(periodEndDate) : dayjs();

    let monthPeriodStartDate = dayjs(periodStartDate);

    while (monthPeriodStartDate.isBefore(maxPeriodEndDate)) {
      const monthPeriodEndDate = dayjs(monthPeriodStartDate).endOf("month");
      periods.push({
        label: t("commissions.points.helpers.namedPeriod", { name: monthPeriodStartDate.format("MMMM YYYY") }),
        startDate: formatLocaleDate(monthPeriodStartDate) ?? "",
        endDate:
          formatLocaleDate(monthPeriodEndDate.isBefore(maxPeriodEndDate) ? monthPeriodEndDate : maxPeriodEndDate) ?? "",
      });
      monthPeriodStartDate = monthPeriodStartDate.startOf("month").add(1, "month");
    }
  } else {
    const endDate = (isTopAgentPlus ? formatLocaleDate(dayjs().subtract(1, "month").endOf("month")) : today) ?? "";

    periods.push(
      {
        label: t("commissions.points.helpers.currentMonthPeriod"),
        startDate:
          (isTopAgentPlus
            ? formatLocaleDate(currentMonthStartDate.subtract(1, "month"))
            : formatLocaleDate(currentMonthStartDate)) ?? "",
        endDate: endDate,
      },
      {
        label: t("commissions.points.helpers.last3MonthsPeriod"),
        startDate:
          (isTopAgentPlus
            ? formatLocaleDate(last3MonthsStartDate.subtract(1, "month"))
            : formatLocaleDate(last3MonthsStartDate)) ?? "",
        endDate: endDate,
      },
      {
        label: t("commissions.points.helpers.last6MonthsPeriod"),
        startDate:
          (isTopAgentPlus
            ? formatLocaleDate(last6MonthsStartDate.subtract(1, "month"))
            : formatLocaleDate(last6MonthsStartDate)) ?? "",
        endDate: endDate,
      },
    );
  }

  if (periodStartDate) {
    const selectedPeriodTitle = (
      <LabelWithTooltip
        label={t("commissions.points.helpers.selectedPeriod")}
        tooltip={t("commissions.points.helpers.productionPeriodDesc", {
          startDate: formatLocaleDate(periodStartDate),
          endDate: periodEndDate ? formatLocaleDate(periodEndDate) : today,
        })}
      />
    );

    columns.push(
      isCommissionAmountsReport
        ? commissionAmountColumn(selectedPeriodTitle, "TOTAL", reportData)
        : {
            key: "selectedPeriod",
            title: selectedPeriodTitle,
            children: [
              {
                ...sortableColumnProps("TOTAL", "INDIVIDUAL", reportData),
                title: t("commissions.points.helpers.individualPointAmount"),
                align: "right",
                width: 135,
                render: (_, record) =>
                  reportType === PointsReportType.NETTO_AND_BRUTTO_POINTS ? (
                    <>
                      <span className="margin-right-tiny">
                        {formatLocaleNettoPoints(record.totalIndividualNettoPointAmount ?? 0)}
                      </span>
                      <br />
                      {formatLocaleBruttoPoints(record.totalIndividualBruttoPointAmount ?? 0)}
                    </>
                  ) : reportType === PointsReportType.NETTO_POINTS ? (
                    formatLocaleNettoPoints(record.totalIndividualNettoPointAmount ?? 0)
                  ) : (
                    formatLocaleBruttoPoints(record.totalIndividualBruttoPointAmount ?? 0)
                  ),
              },
              {
                ...sortableColumnProps("TOTAL", "GROUP", reportData),
                title: t("commissions.points.helpers.groupPointAmount"),
                align: "right",
                width: 135,
                render: (_, record) =>
                  reportType === PointsReportType.NETTO_AND_BRUTTO_POINTS ? (
                    <>
                      <span className="margin-right-tiny">
                        {formatLocaleNettoPoints(record.totalGroupNettoPointAmount ?? 0)}
                      </span>
                      <br />
                      {formatLocaleBruttoPoints(record.totalGroupBruttoPointAmount ?? 0)}
                    </>
                  ) : reportType === PointsReportType.NETTO_POINTS ? (
                    formatLocaleNettoPoints(record.totalGroupNettoPointAmount ?? 0)
                  ) : (
                    formatLocaleBruttoPoints(record.totalGroupBruttoPointAmount ?? 0)
                  ),
              },
              {
                ...sortableColumnProps("TOTAL", "SUM", reportData),
                title: t("commissions.points.helpers.together"),
                align: "right",
                width: 135,
                render: (_, record) => {
                  const dataTogether = getTotalDataTogether(record, reportType);

                  return reportType === PointsReportType.NETTO_AND_BRUTTO_POINTS ? (
                    <>
                      <span className="margin-right-tiny">
                        {formatLocaleNettoPoints(dataTogether.nettoPoints ?? 0)}
                      </span>
                      <br />
                      {formatLocaleBruttoPoints(dataTogether.bruttoPoints ?? 0)}
                    </>
                  ) : reportType === PointsReportType.NETTO_POINTS ? (
                    formatLocaleNettoPoints(dataTogether.nettoPoints ?? 0)
                  ) : (
                    formatLocaleBruttoPoints(dataTogether.bruttoPoints ?? 0)
                  );
                },
              },
            ],
          },
    );
  }

  periods.forEach((period, periodIndex) => {
    const periodTitle = (
      <LabelWithTooltip
        label={period.label}
        tooltip={t("commissions.points.helpers.productionPeriodDesc", {
          startDate: period.startDate,
          endDate: period.endDate,
        })}
      />
    );

    columns.push(
      isCommissionAmountsReport
        ? commissionAmountColumn(periodTitle, "PERIOD", reportData, periodIndex)
        : {
            key: `period${periodIndex}`,
            title: periodTitle,
            children: [
              {
                ...sortableColumnProps("PERIOD", "INDIVIDUAL", reportData, periodIndex),
                title: t("commissions.points.helpers.individualPointAmount"),
                align: "right",
                width: 135,
                render: (_, record) =>
                  reportType === PointsReportType.NETTO_AND_BRUTTO_POINTS ? (
                    <>
                      <span className="margin-right-tiny">
                        {formatLocaleNettoPoints(record.individualNettoPointAmountsByPeriod?.[periodIndex] ?? 0)}
                      </span>
                      <br />
                      {formatLocaleBruttoPoints(record.individualBruttoPointAmountsByPeriod?.[periodIndex] ?? 0)}
                    </>
                  ) : reportType === PointsReportType.NETTO_POINTS ? (
                    formatLocaleNettoPoints(record.individualNettoPointAmountsByPeriod?.[periodIndex] ?? 0)
                  ) : (
                    formatLocaleBruttoPoints(record.individualBruttoPointAmountsByPeriod?.[periodIndex] ?? 0)
                  ),
              },
              {
                ...sortableColumnProps("PERIOD", "GROUP", reportData, periodIndex),
                title: t("commissions.points.helpers.groupPointAmount"),
                align: "right",
                width: 135,
                render: (_, record) =>
                  reportType === PointsReportType.NETTO_AND_BRUTTO_POINTS ? (
                    <>
                      <span className="margin-right-tiny">
                        {formatLocaleNettoPoints(record.groupNettoPointAmountsByPeriod?.[periodIndex] ?? 0)}
                      </span>
                      <br />
                      {formatLocaleBruttoPoints(record.groupBruttoPointAmountsByPeriod?.[periodIndex] ?? 0)}
                    </>
                  ) : reportType === PointsReportType.NETTO_POINTS ? (
                    formatLocaleNettoPoints(record.groupNettoPointAmountsByPeriod?.[periodIndex] ?? 0)
                  ) : (
                    formatLocaleBruttoPoints(record.groupBruttoPointAmountsByPeriod?.[periodIndex] ?? 0)
                  ),
              },
              {
                ...sortableColumnProps("PERIOD", "SUM", reportData, periodIndex),
                title: t("commissions.points.helpers.together"),
                align: "right",
                width: 135,
                render: (_, record) => {
                  const dataTogether = getDataForPeriodTogether(record, periodIndex, reportType);

                  return reportType === PointsReportType.NETTO_AND_BRUTTO_POINTS ? (
                    <>
                      <span className="margin-right-tiny">
                        {formatLocaleNettoPoints(dataTogether.nettoPoints ?? 0)}
                      </span>
                      <br />
                      {formatLocaleBruttoPoints(dataTogether.bruttoPoints ?? 0)}
                    </>
                  ) : reportType === PointsReportType.NETTO_POINTS ? (
                    formatLocaleNettoPoints(dataTogether.nettoPoints ?? 0)
                  ) : (
                    formatLocaleBruttoPoints(dataTogether.bruttoPoints ?? 0)
                  );
                },
              },
            ],
          },
    );
  });

  if (!periodStartDate) {
    columns.push(
      isCommissionAmountsReport
        ? commissionAmountColumn(t("commissions.points.helpers.totalPeriod"), "TOTAL", reportData)
        : {
            key: "total",
            title: t("commissions.points.helpers.totalPeriod"),
            children: [
              {
                ...sortableColumnProps("TOTAL", "INDIVIDUAL", reportData),
                title: t("commissions.points.helpers.individualPointAmount"),
                align: "right",
                width: 135,
                render: (_, record) =>
                  reportType === PointsReportType.NETTO_AND_BRUTTO_POINTS ? (
                    <>
                      <span className="margin-right-tiny">
                        {formatLocaleNettoPoints(record.totalIndividualNettoPointAmount ?? 0)}
                      </span>
                      <br />
                      {formatLocaleBruttoPoints(record.totalIndividualBruttoPointAmount ?? 0)}
                    </>
                  ) : reportType === PointsReportType.NETTO_POINTS ? (
                    formatLocaleNettoPoints(record.totalIndividualNettoPointAmount ?? 0)
                  ) : (
                    formatLocaleBruttoPoints(record.totalIndividualBruttoPointAmount ?? 0)
                  ),
              },
              {
                ...sortableColumnProps("TOTAL", "GROUP", reportData),
                title: t("commissions.points.helpers.groupPointAmount"),
                align: "right",
                width: 135,
                render: (_, record) =>
                  reportType === PointsReportType.NETTO_AND_BRUTTO_POINTS ? (
                    <>
                      <span className="margin-right-tiny">
                        {formatLocaleNettoPoints(record.totalGroupNettoPointAmount ?? 0)}
                      </span>
                      <br />
                      {formatLocaleBruttoPoints(record.totalGroupBruttoPointAmount ?? 0)}
                    </>
                  ) : reportType === PointsReportType.NETTO_POINTS ? (
                    formatLocaleNettoPoints(record.totalGroupNettoPointAmount ?? 0)
                  ) : (
                    formatLocaleBruttoPoints(record.totalGroupBruttoPointAmount ?? 0)
                  ),
              },
              {
                ...sortableColumnProps("TOTAL", "SUM", reportData),
                title: t("commissions.points.helpers.together"),
                align: "right",
                width: 135,
                render: (_, record) => {
                  const dataTogether = getTotalDataTogether(record, reportType);

                  return reportType === PointsReportType.NETTO_AND_BRUTTO_POINTS ? (
                    <>
                      <span className="margin-right-tiny">
                        {formatLocaleNettoPoints(dataTogether.nettoPoints ?? 0)}
                      </span>
                      <br />
                      {formatLocaleBruttoPoints(dataTogether.bruttoPoints ?? 0)}
                    </>
                  ) : reportType === PointsReportType.NETTO_POINTS ? (
                    formatLocaleNettoPoints(dataTogether.nettoPoints ?? 0)
                  ) : (
                    formatLocaleBruttoPoints(dataTogether.bruttoPoints ?? 0)
                  );
                },
              },
            ],
          },
    );
  }

  return { columns, periods };
};

const today = formatLocaleDate(dayjs());
const currentMonthStartDate = dayjs().startOf("month");
const last3MonthsStartDate = dayjs().subtract(2, "month").startOf("month");
const last6MonthsStartDate = dayjs().subtract(5, "month").startOf("month");

type Props = {
  searchParams: PointsReportFilterPageRequest;
};

export const PointsReportTable = ({ searchParams }: Props) => {
  const { t } = useTranslation();

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const { data: reportData, isFetching } = useFilterPointsReport(searchParams);
  const downloadPointsReportExport = useDownloadPointsReportExport();

  const updateSearchParams = useUpdateFilterSearchParams<PointsReportFilterPageRequest>(DEFAULT_POINTS_QUERY_PARAMS);

  const tableData = getTableData(isTopAgentPlus, reportData);

  return (
    <Card className="card-box" style={{ position: "relative" }}>
      <div className={reportData?.pageData.length ? "table-header-export-actions" : "margin-bottom-small"}>
        <ActionTextIcon
          icon="download"
          color="blue"
          text={t("common.exportXlsx")}
          onClick={() => downloadPointsReportExport.mutate({ ...searchParams, exportFileType: ExportFileType.XLSX })}
        />

        <Divider orientation="vertical" />

        <ActionTextIcon
          icon="download"
          color="green"
          text={t("common.exportCsv")}
          onClick={() => downloadPointsReportExport.mutate({ ...searchParams, exportFileType: ExportFileType.CSV })}
        />
      </div>

      <Table<NettoAndBruttoPointsBalance>
        {...tableStandardProps()}
        rowKey={(record) => record.agent.id}
        columns={tableData.columns}
        scroll={{
          x:
            reportData?.reportType === PointsReportType.COMMISSION_AMOUNTS
              ? Math.max(TableSizes.XXL, 710 + tableData.periods.length * 260)
              : 710 + tableData.periods.length * 280,
        }}
        dataSource={reportData?.pageData}
        loading={isFetching}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(searchParams.pageIndex) + 1,
          pageSize: reportData?.pageSize,
          total: reportData?.totalElementsCount,
        }}
        onChange={(pagination, __, sorter) => {
          const sorters = Array.isArray(sorter) ? sorter : [sorter];

          const orderBy: PointsReportOrderByColumn[] = [];
          const orderDirections: OrderDirection[] = [];
          // The API sorts all period columns by the same period, so a single index is sent for the whole request.
          let orderByPeriodIndex: number | undefined;

          sorters.forEach((sorterItem) => {
            const columnSorting = sorterItem.column as PointsReportColumnSorting | undefined;

            if (!columnSorting?.orderBy || !sorterItem.order) {
              return;
            }

            orderBy.push(...columnSorting.orderBy);
            orderDirections.push(getOrderDirection(sorterItem.order));
            orderByPeriodIndex = columnSorting.orderByPeriodIndex ?? orderByPeriodIndex;
          });

          updateSearchParams(
            {
              orderBy,
              orderDirections,
              orderByPeriodIndex,
              pageIndex: pagination.current ? pagination.current - 1 : undefined,
            },
            true,
          );
        }}
        sticky={{ offsetHeader: 54 }}
      />
    </Card>
  );
};
